import React, { useCallback, useEffect, useState } from 'react';
import { DialogContent, Button, ButtonProps, Badge } from '@material-ui/core';
import { StyledDialog } from '../components';
import { FormHub, useFormEvents } from '../forms';
import { useHistory } from 'react-router-dom';
import {
  FormStatus,
  Reservation,
  FormEvent,
  RoomRateForms,
  bulkAvailableService,
} from '@lib/state';
import { BulkChangeDatesReservationsForm } from './forms';

interface BulkFormFields {
  checkInDate: Date | undefined;
  checkOutDate: Date | undefined;
}

interface Props extends Omit<ButtonProps, 'action' | 'onClick'> {
  propertyId: string;
  corporateAccountId: string;
  selectedReservations: Array<Reservation>;
}

const BulkChangeDatesButtonComponent: React.FC<Props> = ({
  propertyId,
  corporateAccountId,
  selectedReservations,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [{ status, error }, resetBulkAvailableRatesFom] = useFormEvents(
    RoomRateForms.BulkAvailableRates
  );

  const history = useHistory();
  const targetPage = '/profile/bulk-change-dates';

  const closeDialog = () => {
    resetBulkAvailableRatesFom();
    setOpen(false);
  };

  const sendToPage = () => {
    history.push({ pathname: targetPage });
  };

  useEffect(() => {
    if (status === FormStatus.Success) {
      closeDialog();
      sendToPage();
    }
  }, [status]);

  return (
    <>
      <Badge badgeContent={selectedReservations.length} color="error" style={{ marginLeft: '8px' }}>
        {/* It is not actually an error but it produces the red color which helps making sure the user does not miss it */}
        <Button
          type="button"
          name="bulkChangeDatesReservation"
          onClick={() => setOpen(true)}
          {...props}
        >
          Change Dates
        </Button>
      </Badge>
      <StyledDialog
        title="Bulk Change Dates"
        open={open}
        onClose={() => closeDialog()}
        disablePortal
        maxWidth="md"
      >
        <DialogContent>
          <BulkChangeDatesReservationsFormContainer
            propertyId={propertyId}
            corporateAccountId={corporateAccountId}
            reservations={selectedReservations}
            status={status}
            error={error}
          />
        </DialogContent>
      </StyledDialog>
    </>
  );
};

const BulkChangeDatesReservationsFormContainer: React.FC<
  {
    propertyId: string;
    corporateAccountId: string;
    reservations: Array<Reservation>;
    onDatesChanged?: (checkInDate?: Date, checkOutDate?: Date) => void;
  } & Pick<FormEvent, 'status' | 'error'>
> = ({ propertyId, corporateAccountId, reservations, error, onDatesChanged }) => {
  const onSubmit = useCallback(
    ({ checkInDate, checkOutDate }: BulkFormFields) => {
      if (onDatesChanged) onDatesChanged(checkInDate, checkOutDate);
      bulkAvailableService.getBulkAvailability(
        propertyId,
        corporateAccountId,
        reservations,
        checkInDate,
        checkOutDate
      );
    },
    [propertyId, corporateAccountId, reservations]
  );

  return (
    <FormHub onSubmit={onSubmit}>
      <BulkChangeDatesReservationsForm
        reservations={reservations}
        corporateAccountId={corporateAccountId}
        propertyId={propertyId}
        error={error}
      />
    </FormHub>
  );
};

export const BulkChangeDatesButton = BulkChangeDatesButtonComponent;
